// used by search page and home paeg to display search results
import React, {
  Children,
  PropsWithChildren,
  ReactElement,
  cloneElement,
  useState,
  useRef,
  useMemo,
} from "react";
import SearchBox, { SearchBoxProps } from "./SearchBox";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useTagsStore } from "../../store/tagsStore";

interface DropdownSearchBoxProps extends SearchBoxProps {
  className?: string;
  searchBoxClassName?: string;
}
// this component is
const DropdownSearchBox = ({
  children,
  className,
  searchBoxClassName,
  ...rest
}: PropsWithChildren<DropdownSearchBoxProps>) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate(); // Initialize navigate

  const providerLocation = useTagsStore((state) => state.providerLocation);
  const providerCategories = useTagsStore((state) => state.providerCategories);

  const allLocations = useMemo(() => {
    return providerLocation.map((location) => ({
      name: "location",
      value: location.id,
      label: location.name,
    }));
  }, []);

  const allCategories = useMemo(() => {
    return providerCategories.map((category) => ({
      name: "category",
      value: category.id,
      label: category.name,
    }));
  }, []);

  // Function to handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission
    const searchValue = searchInputValue.trim();

    // Split the search value by commas and trim whitespace.
    // we wil seperate the input into locations and categories based on whether they exist in allLocations or allCategories. so we will take the search value and if it exists in allLocations, we will add it to the locations array, if it exists in allCategories, we will add it to the categories array. if it does not exist in either, we will add it to the categories array.
    const tokens = searchValue
      .split(",")
      .map((token) => token.trim())
      .filter((token) => token !== "");

    const locations: string[] = [];
    const categories: string[] = [];
    // const subCategories: string[] = [];
    const customUserSearch: string[] = [];

    tokens.forEach((token) => {
      if (allLocations.some((element) => element.label === token)) {
        locations.push(token);
      } else if (allCategories.some((element) => element.label === token)) {
        categories.push(token);
        // } else if (allSubCategories.some((subCat) => subCat.label === token)) {
        //   subCategories.push(token);
      } else {
        customUserSearch.push(token);
      }
    });

    // Construct the query parameters
    const params = new URLSearchParams();
    if (locations.length > 0) {
      params.set("location", locations.join(","));
    }
    if (categories.length > 0) {
      params.set("category", categories.join(","));
    }
    // if (subCategories.length > 0) {
    //   params.set("subCategory", subCategories.join(","));
    // }
    if (customUserSearch.length > 0) {
      params.set("userQuery", customUserSearch.join(","));
    }

    // Navigate to the filter page with the search query
    navigate(`/home/searchpage?${params.toString()}`);

    setOpenDropdown(false);
  };

  // Function to handle selection of a suggestion
  const handleSelectSuggestion = (suggestion: string) => {
    // Split the current input into parts
    const parts = searchInputValue.split(",");
    // Remove any empty strings and trim whitespace
    const trimmedParts = parts.map((p) => p.trim()).filter((p) => p.length > 0);

    // Replace the last part with the selected suggestion or add it
    if (parts.length > 0) {
      trimmedParts[trimmedParts.length - 1] = suggestion;
    } else {
      trimmedParts.push(suggestion);
    }

    // Reconstruct the input value with a trailing comma and space
    const newValue = trimmedParts.join(", ") + ", ";
    setSearchInputValue(newValue);
    // Keep the dropdown open so they can add more
    setOpenDropdown(true);
    // Focus the search box
    if (searchBoxRef.current) {
      searchBoxRef.current.focus();
    }
  };

  return (
    <Dropdown
      className={className}
      show={openDropdown}
      onToggle={(isOpen) => setOpenDropdown(isOpen)}
    >
      <Dropdown.Toggle
        as="div"
        aria-expanded={openDropdown}
        bsPrefix="toggle"
        size="sm"
      >
        <div className="position-relative">
          <SearchBox
            placeholder="Buscar..."
            className={searchBoxClassName}
            value={searchInputValue}
            ref={searchBoxRef}
            onChange={({ target }) => {
              const value = target.value;
              setSearchInputValue(value);
              // Only open dropdown if there’s real (non-whitespace) input
              setOpenDropdown(value.trim().length > 0);
            }}
            onSubmit={handleSubmit}
            {...rest}
          />
          {searchInputValue.trim().length > 0 && (
            <button
              type="button"
              onClick={() => {
                setSearchInputValue("");
                setOpenDropdown(false);
              }}
              style={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "1.2rem",
                fontWeight: "bold",
                lineHeight: 1,
                color: "#999",
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </Dropdown.Toggle>

      {/* Only render the dropdown menu when real input exists */}
      {searchInputValue.trim().length > 0 && children && (
        <Dropdown.Menu className="dropdown-menu border border-300 font-base start-0 py-0 overflow-hidden w-80">
          {Children.map(children, (child) =>
            cloneElement(child as ReactElement, {
              searchValue: searchInputValue,
              onSelectSuggestion: handleSelectSuggestion,
            })
          )}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

export default DropdownSearchBox;
